import CheckIcon from '@mui/icons-material/Check';
import { Checkbox } from 'common/components/Checkbox/Checkbox';
import { Contractor } from 'common/models';
import { useCallback, useMemo, useState } from 'react';
import { UseFormRegister } from 'react-hook-form';
import { Column } from 'react-table';
import styled from 'styled-components';
import { ContractorValues } from '../components/Sub-views/ContractorsView/ContractorsView';
import {
  CenteredHeader,
  CenteredText,
} from '../components/Sub-views/RatesView/Tabs/styles';
import { AddSettingInput } from '../components/Sub-views/styles';

const SizedCenteredText = styled(CenteredText)`
  height: 24px;
`;

const VendorIdInput = styled(AddSettingInput)`
  text-transform: uppercase;
`;

export type ContractorRows = {
  code: string;
  contractorName: string;
  vendorId: { displayValue: string; formValueKey: string };
  active: { displayValue: boolean; formValueKey: string };
};

export const useContractorsTableData: {
  (
    rows: Contractor[],
    register: UseFormRegister<ContractorValues>,
    isSubmitting: boolean,
  ): {
    columns: Column<ContractorRows>[];
    data: ContractorRows[];
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
  };
} = (rows, register, isSubmitting) => {
  const [isEditing, setIsEditing] = useState(false);

  const VendorIdInputWrapper = useCallback(
    ({ value }: { value: { displayValue: string; formValueKey: string } }) => {
      return isEditing ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', height: '24px' }}
        >
          <VendorIdInput
            {...register(`${value.formValueKey}.vendorId`)}
            disabled={isSubmitting}
            $isError={false}
          />
        </div>
      ) : (
        <SizedCenteredText>{value.displayValue}</SizedCenteredText>
      );
    },
    [isEditing, isSubmitting, register],
  );

  const buildActiveColumn: () => Column<ContractorRows> = useCallback(
    () => ({
      Header: () => <CenteredHeader>Active</CenteredHeader>,
      accessor: 'active',
      width: '15%',
      Cell: ({ value }) => (
        <SizedCenteredText>
          {isEditing ? (
            <Checkbox
              formProps={{ name: `${value.formValueKey}.active` }}
              isDisabled={isSubmitting}
            />
          ) : (
            <>{value.displayValue && <CheckIcon />}</>
          )}
        </SizedCenteredText>
      ),
    }),
    [isEditing, isSubmitting],
  );

  const columns: Column<ContractorRows>[] = useMemo(
    () => [
      {
        Header: () => <CenteredHeader>Code</CenteredHeader>,
        accessor: 'code',
        width: '15%',
        Cell: ({ value }) => <SizedCenteredText>{value}</SizedCenteredText>,
      },
      {
        Header: () => <CenteredHeader>Name</CenteredHeader>,
        accessor: 'contractorName',
        width: '40%',
        Cell: ({ value }) => <SizedCenteredText>{value}</SizedCenteredText>,
      },
      {
        Header: () => <CenteredHeader>Vendor ID</CenteredHeader>,
        accessor: 'vendorId',
        width: '30%',
        Cell: ({ value }) => <VendorIdInputWrapper value={value} />,
      },
      buildActiveColumn(),
    ],
    [VendorIdInputWrapper, buildActiveColumn],
  );

  const data: ContractorRows[] = useMemo(
    () =>
      rows.map(({ id, code, contractorName, vendorId, active }) => ({
        code,
        contractorName,
        vendorId: {
          displayValue: vendorId || '',
          formValueKey: id.toString(),
        },
        active: { displayValue: active, formValueKey: id.toString() },
      })),
    [rows],
  );

  return {
    columns,
    data,
    isEditing,
    setIsEditing,
  };
};
